import { useEffect, useState } from 'react';
import { api, type Template } from '../lib/api';

// The template store (192): a gallery every creator sees — plates, rigs,
// frames, scenes, elements, audio, backgrounds — free to take, made to be
// changed. Cards like a document editor's gallery: a poster, a name, a kind.
// Taking a recipe kind puts a look in the show's Lab in the show's colours;
// an element goes onto the show's kit; audio or a background is copied into
// the library — and into this episode's collection when opened from one.
// A template exists already, so taking one costs a render, never a picture.

const KINDS: Array<{ id: Template['kind'] | 'all'; label: string }> = [
  { id: 'all', label: 'All' }, { id: 'plate', label: 'Plates' }, { id: 'rig', label: 'Rigs' }, { id: 'frame', label: 'Frames' },
  { id: 'scene', label: 'Scenes' }, { id: 'element', label: 'Elements' }, { id: 'audio', label: 'Audio' }, { id: 'background', label: 'Backgrounds' },
];

export function TemplateGallery({ brandId, cardId, onTaken, compact = false }: {
  brandId: string | null;
  /** The episode to file a taken audio or background into. */
  cardId?: string;
  onTaken?: (t: Template, result: { asset?: { id: string }; look?: { id: string } }) => void;
  compact?: boolean;
}) {
  const [items, setItems] = useState<Template[]>([]);
  const [kind, setKind] = useState<Template['kind'] | 'all'>('all');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [posters, setPosters] = useState<Record<string, string>>({});
  useEffect(() => {
    for (const t of items) if (t.poster && !posters[t.id]) api.templatePoster(t.id).then((u) => { if (u) setPosters((m) => ({ ...m, [t.id]: u })); }).catch(() => undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);
  useEffect(() => {
    let live = true;
    api.templates(kind === 'all' ? undefined : kind).then((t) => { if (live) setItems(t); }).catch((e) => setError((e as Error).message));
    return () => { live = false; };
  }, [kind]);

  async function take(t: Template) {
    setBusy(t.id); setError(null); setNote(null);
    try {
      const r = await api.takeTemplate(t.id, { brandId, cardId });
      setNote(r.look ? `${t.name} is in the Lab, in this show's colours.` : r.element ? `${t.name} is on the show's kit — offer it from "+ tag".` : cardId ? `${t.name} is in this episode's collection, and the library.` : `${t.name} is in the library under Templates.`);
      onTaken?.(t, r);
    } catch (e) { setError((e as Error).message); } finally { setBusy(null); }
  }

  const needsShow = (t: Template) => t.kind !== 'audio' && t.kind !== 'background';
  return (
    <div data-tool="templates" style={{ display: 'grid', gap: 10 }}>
      <div className="row wrap" style={{ gap: 4, alignItems: 'center' }}>
        {KINDS.map((k) => <button key={k.id} type="button" className={`btn tiny${kind === k.id ? ' pri' : ''}`} onClick={() => setKind(k.id)}>{k.label}</button>)}
        <span style={{ flex: 1 }} />
        <span className="sub-dim" style={{ fontSize: 12 }}>Free to take. A taken template costs a render, never a picture.</span>
      </div>
      {error && <div className="err inline">{error}</div>}
      {note && <div className="sub-dim">{note}</div>}
      {items.length === 0 && <p className="hint" style={{ margin: 0 }}>Nothing here yet for that kind. Share a look from the Lab and it joins the store.</p>}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${compact ? 150 : 190}px, 1fr))`, gap: 10 }}>
        {items.map((t) => (
          <div key={t.id} className="card" style={{ display: 'grid', gap: 6, padding: 8 }} title={t.blurb ?? t.name}>
            <div style={{ aspectRatio: '16 / 9', borderRadius: 6, overflow: 'hidden', background: 'var(--surface-3, #222)', display: 'grid', placeItems: 'center' }}>
              {posters[t.id] ? <img src={posters[t.id]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <span className="sub-dim" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '.08em' }}>{t.kind}</span>}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
              <div className="sub-dim" style={{ fontSize: 11 }}>{t.kind} · {t.licence === 'creator-shared' ? `shared by ${t.creator}` : t.licence === 'cc0' ? 'CC0' : 'RookeryOS'}</div>
            </div>
            <button type="button" className="btn tiny pri" disabled={busy === t.id || (needsShow(t) && !brandId)}
                    title={needsShow(t) && !brandId ? 'Choose a show first — a recipe is taken in its colours' : t.kind === 'element' ? 'Onto the show\'s kit' : needsShow(t) ? 'Into the show\'s Lab, in its colours' : cardId ? 'Into this episode and the library' : 'Into the library'}
                    onClick={() => void take(t)}>
              {busy === t.id ? 'Taking…' : 'Take it'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
